import { getSessionId } from './useAuthApi'

/**
 * Bonus / voucher API layer.
 *
 * Wraps the four player-facing edge functions of the coupon-activated bonus
 * system:
 *   - coupon-validate         (preview a voucher before redeeming)
 *   - coupon-redeem           (attach a voucher to the account)
 *   - bonus-withdrawal-status (active grants + wagering progress)
 *   - bonus-forfeit-all       (burn all active bonus, unblock withdrawal)
 *
 * All four require a Bearer JWT (unlike balance-get which is session-only).
 * Domain error codes come back in DIFFERENT fields depending on the endpoint
 * (coupon → `code`, forfeit → `error`); callers should read via
 * getApiErrorMessage / localizeErrorCode which already probe all shapes.
 */

// ---- Coupon validate ----------------------------------------------------

export interface CouponPreview {
  code: string
  name: string
  description: string | null
  type: 'fixed_bonus' | 'deposit_match_percent' | 'free_bet'
  wageringMultiplier: number
  expiresAt: string | null
  bonusAmount?: number    // fixed_bonus
  matchPercent?: number   // deposit_match_percent (value * 100)
  minDeposit?: number | null
  maxBonus?: number | null
  freeBetAmount?: number  // free_bet
  // Bonus-play terms (coupon-level). minCashoutMultiplier is a plain multiplier
  // (Cash Out locked below it while the bonus is active); maxConversionAmount is
  // USD (like minDeposit/maxBonus) — the cap on how much bonus converts to
  // withdrawable once wagering completes.
  minCashoutMultiplier?: number | null
  maxConversionAmount?: number | null
}

export interface ValidateCouponResponse {
  valid: boolean
  coupon?: CouponPreview
}

export async function validateCoupon(accessToken: string, code: string): Promise<ValidateCouponResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch<ValidateCouponResponse>(`${config.public.apiBase}/coupon-validate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { code },
  })
}

// ---- Coupon redeem ------------------------------------------------------

export interface RedeemCouponResponse {
  success: boolean
  coupon: { code: string; name: string; type: CouponPreview['type'] }
  bonus?: { amount: number; wageringRequired: number }
  depositMatch?: { matchPercent: number; minDeposit: number | null; maxBonus: number | null; expiresAt: string }
}

export async function redeemCoupon(accessToken: string, code: string): Promise<RedeemCouponResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch<RedeemCouponResponse>(`${config.public.apiBase}/coupon-redeem`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { code },
  })
}

// ---- Cancel pending deposit-match voucher ------------------------------

export interface CancelPendingCouponResponse {
  success: boolean
  reason?: string
}

/**
 * Detach a pending (not-yet-funded) deposit-match voucher so the code can be
 * used again. No-op (success:false) if there's nothing pending — e.g. the match
 * already funded, or another tab detached it first.
 */
export async function cancelPendingCoupon(accessToken: string, code: string): Promise<CancelPendingCouponResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch<CancelPendingCouponResponse>(`${config.public.apiBase}/coupon-cancel-pending`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { code },
  })
}

// ---- Wagering / withdrawal status --------------------------------------

export interface BlockingGrant {
  grantId: string
  grantType: string
  remainingAmount: string
  wageringRemaining: string
  percentComplete: string
  /** Raw wagering totals (XAUT), so the client shows an exact "$done of $required"
   * and progress without reconstructing required from the rounded percentComplete
   * (which collapses to $0 near 100%). Optional for backward compatibility with a
   * status payload from before these fields were added. */
  wageringRequired?: string
  wageringProgress?: string
  minCashoutMultiplier: number | null
}

/** A pending (not-yet-funded) deposit-match voucher, from the wagering status. */
export interface PendingDepositMatchVoucher {
  code: string
  name: string
  matchPercent: number
  minDeposit: number | null
  maxBonus: number | null
  wageringMultiplier: number
}

export interface WageringStatus {
  canWithdraw: boolean
  activeGrantCount: number
  totalWageringRemaining: string
  totalBonusAtRisk: string
  blockingGrants: BlockingGrant[]
  // Binding min cash-out multiplier across active grants (null = no floor).
  minCashoutMultiplier: number | null
  // Pending deposit-match voucher (grant still awaiting the qualifying deposit),
  // absent from blockingGrants — lets the deposit field restore its state after
  // a reload / in a new session. null = none.
  pendingDepositMatch?: PendingDepositMatchVoucher | null
  message?: string
}

export async function fetchWageringStatus(accessToken: string): Promise<WageringStatus> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch<WageringStatus>(`${config.public.apiBase}/bonus-withdrawal-status`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })
}

// ---- Forfeit all --------------------------------------------------------

export interface ForfeitAllResponse {
  success: boolean
  grantsForfeited: number
  totalAmountForfeited: string
  grantIds: string[]
  canWithdrawNow: boolean
  message: string
}

export async function forfeitAllBonuses(accessToken: string, reason?: string): Promise<ForfeitAllResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch<ForfeitAllResponse>(`${config.public.apiBase}/bonus-forfeit-all`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { confirmForfeit: true, ...(reason && { reason }) },
  })
}
